import { useSelector } from 'react-redux';
import { selectors } from 'darwin_core';
import {
  humanizeTemperature,
  makeDhwModeNameHumanized,
} from 'darwin_core/utils/helpers.js';

import BackLink from '../navigation/BackLink.jsx';
import { homePath } from '../navigation/paths.js';
import { useZoneRouteContext } from '../navigation/ZoneRoute.jsx';
import HotWaterDial from '../zone/HotWaterDial.jsx';
import SetpointDial from '../zone/SetpointDial.jsx';
import ZoneActions from '../zone/ZoneActions.jsx';
import CancelOverride from '../controls/CancelOverride.jsx';
import { ZoneHolidayBanner } from '../components/HolidayBanner.jsx';
import { OfflineHelp, useZoneOfflineHelp } from '../components/OfflineHelp.jsx';
import { FireIcon, SpinnerIcon } from '../components/icons.jsx';
import useScreen from '../hooks/useScreen.js';

// Zone detail — the locked app-style layout.
//
// A zone shows a dial on the LEFT and a vertical action list on the RIGHT
// (Heating Status · Schedule · Plus Hours); it collapses to a single column on a
// narrow screen. Which dial depends on the zone: a heating zone gets the draggable
// setpoint Radial, a hot-water zone the read-only HotWaterDial (a cylinder has no
// user setpoint, so the app shows the live On/Off in the same circle instead).
// Both keep the same three actions (product decision: full app parity with the
// mobile hot-water zone).
//
// The cancel-override exit sits at the top of the action column and is the single
// place a zone in an override (OverrideNext / OverridePlus / DSR) is released. It
// renders nothing when the zone is not overridden, and is not duplicated on the
// dedicated action screens.
//
// While this zone's thermostat is in holiday the dial is disabled and every action
// screen locks its controls; the app makes the zone unreachable instead (its tile
// is blocked), but a browser user can arrive here by URL, so the Holiday notice
// under the header says why and links to the Holiday screen. It reads THIS
// thermostat's holiday state — in a two-zone home the other zone may not be away.
//
// When the zone is unreachable the action column is replaced by the offline help,
// which names the link that is down (cloud → Wi-Fi, thermostat → zigbee/battery)
// and its steps; the app makes the same swap. A holiday notice can stand above it:
// both facts are true, and only the web can be on this screen for a zone that is
// away, so the notice is not withheld.
export default function ZoneScreen() {
  const { homeId, zoneId } = useZoneRouteContext();
  const name = useSelector((state) =>
    selectors.getPrtFriendlyName(state, homeId, zoneId),
  );
  const isDhw = useSelector((state) => selectors.isDhw(state, homeId, zoneId));
  const heading = name || (isDhw ? 'Hot water' : 'Heating');
  const headingRef = useScreen(heading);

  const currentTemp = useSelector((state) =>
    selectors.getCurrentTemperature(state, homeId, zoneId),
  );
  // A cylinder reports no temperature, so its header sub-line carries the current
  // DHW MODE instead (the app's HotWaterHeader: "Current Status | Auto"). That is
  // the commanded mode — Off / On / Auto / Boost — not the live On/Off the dial
  // shows, and it reads "Offline" when the zone is down, again as the app does.
  const dhwMode = useSelector((state) =>
    isDhw
      ? makeDhwModeNameHumanized(selectors.getMode(state, homeId, zoneId))
      : '',
  );
  const online = useSelector((state) =>
    selectors.isZoneOnline(state, homeId, zoneId),
  );
  // The flame status marker (top-right) mirrors the app: shown while the zone is
  // actively calling for heat. isBoilerHeating reads zone.flame and can touch a
  // possibly-missing zone, so guard it; a hot-water zone reports no flame → off.
  const heating = useSelector((state) => {
    try {
      return selectors.isBoilerHeating(state, homeId, zoneId);
    } catch {
      return 'off';
    }
  });
  // A control command for this home is still on its way — the app's header
  // "Sending request" / "Updating information" cue (darwin_core's
  // isCommandInFlight). It tells the user when it is safe to leave, and is a cue
  // only: the controls stay live, since darwin_core takes a new command on top
  // of one still out. It clears on the cloud's ack for a device-service home, or
  // when the legacy saga queue drains. Home-level like the app's: a command for
  // this home's other zone spins here too. Not keyed on the zone's pending
  // slice, which on a device-service home would hold the spinner for the whole
  // 30s timeout.
  const loading = useSelector((state) =>
    selectors.isCommandInFlight(state, homeId),
  );
  // Whether this zone is unreachable, and which link is down. The block it feeds
  // takes the place of the action column exactly as it does in the app's zone
  // screens — the dial stays, already reading "Offline".
  const offlineHelp = useZoneOfflineHelp(homeId, zoneId);

  return (
    <main className="app-shell screen zone-screen">
      <header className="screen__header">
        <BackLink to={homePath(homeId)} label="Property" />
        <div className="zone-header">
          <div className="zone-header__titles">
            <h1
              className="zone-header__name"
              id="main-content"
              tabIndex={-1}
              ref={headingRef}
            >
              {heading}
            </h1>
            {isDhw ? (
              <p className="zone-header__sub">
                Current Status | {online ? dhwMode : 'Offline'}
              </p>
            ) : (
              typeof currentTemp === 'number' && (
                <p className="zone-header__sub">
                  Current Room Temperature |{' '}
                  {humanizeTemperature(currentTemp, true, false, false)}
                </p>
              )
            )}
          </div>
          <div className="zone-header__status">
            {loading && (
              <span
                className="zone-header__spinner"
                role="status"
                aria-label="Updating"
              >
                <SpinnerIcon />
              </span>
            )}
            {heating !== 'off' && (
              <span
                className="zone-header__flame"
                role="img"
                aria-label="Calling for heat"
              >
                <FireIcon />
              </span>
            )}
          </div>
        </div>
      </header>
      <ZoneHolidayBanner
        homeId={homeId}
        zoneId={zoneId}
        detail="Cancel Holiday to change this zone's settings."
      />
      <div className="zone-screen__body">
        <div className="zone-screen__dial">
          {isDhw ? (
            <HotWaterDial homeId={homeId} zoneId={zoneId} />
          ) : (
            <SetpointDial homeId={homeId} zoneId={zoneId} />
          )}
        </div>
        <div className="zone-screen__actions">
          {offlineHelp.show ? (
            <OfflineHelp {...offlineHelp} />
          ) : (
            <>
              <CancelOverride homeId={homeId} zoneId={zoneId} />
              <ZoneActions homeId={homeId} zoneId={zoneId} />
            </>
          )}
        </div>
      </div>
    </main>
  );
}
